// The ask_user_question contract, as the backend's harness defines it: what the
// model asks (the tool input), what the card sends back (the tool output), and
// what a settled part holds after a reload (the result the model read).

export const QUESTION_TOOL_NAME = "ask_user_question";
export const QUESTION_PART_TYPE = `tool-${QUESTION_TOOL_NAME}`;

// Bounded on the server too; longer is refused there.
export const MAX_CUSTOM_ANSWER_LENGTH = 2000;

export type QuestionOption = { label: string; description?: string | null };

export type Question = {
  header: string;
  question: string;
  options: QuestionOption[];
  multiSelect: boolean;
};

export type SubmittedAnswer =
  | { header: string; selected: string[] }
  | { header: string; custom_answer: string };

export type SubmittedResponse =
  | { answers: SubmittedAnswer[] }
  | { cancelled: true };

// One question as a settled part shows it: what was asked and what came back.
export type SettledAnswer = { question: Question; answer: string[] };

export type SettledQuestions =
  | { kind: "answered"; answers: SettledAnswer[] }
  | { kind: "skipped"; questions: Question[] }
  | { kind: "not-answered"; questions: Question[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseOption(value: unknown): QuestionOption | null {
  if (!isRecord(value) || typeof value.label !== "string") return null;
  const description =
    typeof value.description === "string" ? value.description.trim() : null;
  return { label: value.label.trim(), description };
}

function parseQuestion(value: unknown): Question | null {
  if (
    !isRecord(value) ||
    typeof value.header !== "string" ||
    typeof value.question !== "string" ||
    !Array.isArray(value.options)
  )
    return null;
  const options = value.options.map(parseOption);
  if (options.length < 2 || options.some((o) => o === null)) return null;
  // The backend strips these before it checks an answer, so the card shows and
  // sends them stripped too.
  return {
    header: value.header.trim(),
    question: value.question.trim(),
    options: options as QuestionOption[],
    multiSelect: value.multi_select === true,
  };
}

// The questions of one call, or null when the input is not a whole call yet
// (still streaming) or not one at all.
export function parseQuestions(input: unknown): Question[] | null {
  if (!isRecord(input) || !Array.isArray(input.questions)) return null;
  const questions = input.questions.map(parseQuestion);
  if (questions.length === 0 || questions.some((q) => q === null)) return null;
  return questions as Question[];
}

function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.some((v) => typeof v !== "string"))
    return null;
  return value as string[];
}

// The card's own envelope, read back while the turn is live.
function fromSubmitted(
  questions: Question[],
  output: Record<string, unknown>
): SettledQuestions | null {
  if (output.cancelled === true) return { kind: "skipped", questions };
  if (!Array.isArray(output.answers)) return null;
  const byHeader = new Map<string, string[]>();
  for (const answer of output.answers) {
    if (!isRecord(answer) || typeof answer.header !== "string") return null;
    if (typeof answer.custom_answer === "string") {
      byHeader.set(answer.header, [answer.custom_answer]);
      continue;
    }
    const selected = stringList(answer.selected);
    if (!selected) return null;
    byHeader.set(answer.header, selected);
  }
  return answeredFrom(questions, byHeader);
}

function answeredFrom(
  questions: Question[],
  byHeader: Map<string, string[]>
): SettledQuestions {
  return {
    kind: "answered",
    answers: questions.map((question) => ({
      question,
      answer: byHeader.get(question.header) ?? [],
    })),
  };
}

// What a settled question part shows, from either the card's envelope (live)
// or the result the model read (reload): a header-to-answers map, or the
// sentence the backend stores when the user skipped or moved on.
export function settledQuestions(
  input: unknown,
  output: unknown,
  errored: boolean
): SettledQuestions | null {
  const questions = parseQuestions(input);
  if (!questions) return null;
  if (errored) return { kind: "not-answered", questions };
  if (typeof output === "string") {
    return /declined/i.test(output)
      ? { kind: "skipped", questions }
      : { kind: "not-answered", questions };
  }
  if (!isRecord(output)) return null;
  if ("answers" in output || "cancelled" in output) {
    return fromSubmitted(questions, output);
  }
  const byHeader = new Map<string, string[]>();
  for (const [header, value] of Object.entries(output)) {
    const answers = stringList(value);
    if (!answers) return null;
    byHeader.set(header, answers);
  }
  return answeredFrom(questions, byHeader);
}

// The banner code the worker sends when it refuses an answer.
export const QUESTION_REJECTED_CODE = "question_answer_rejected";
